import { useState } from "react";
import { Plus, Pencil, Trash2, Pill } from "lucide-react";
import {
  useMedications,
  useDeleteMedication,
  type MedicationSummary,
} from "../../lib/api/useMedications";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { FREQUENCY_LABELS } from "@shared/constants/frequency";
import { MedicationDialog } from "./MedicationDialog";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SectionCard } from "../ui/SectionCard";
import { IconChip } from "../ui/IconChip";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS, ROW } from "../../lib/surfaces";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

function formatExpiration(iso: string | null): { label: string; expired: boolean } {
  if (!iso) return { label: "No expiration", expired: false };
  const date = new Date(iso);
  const now = new Date();
  const expired = date.getTime() < now.getTime();
  const label = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return { label: expired ? `Expired ${label}` : `Expires ${label}`, expired };
}

export function MedicationList({ childId }: { childId: string }) {
  const { data: medications, isLoading, error } = useMedications(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<MedicationSummary | undefined>();
  const [deleting, setDeleting] = useState<MedicationSummary | undefined>();
  const deleteMutation = useDeleteMedication(childId);
  const { publish } = useToast();

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      publish({ variant: "success", title: "Medication deleted successfully" });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to delete medication",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  return (
    <SectionCard
      icon={Pill}
      title="Medications & Treatments"
      accent={SECTION_ACCENT.medications}
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add Medication
        </Button>
      }
    >
      {isLoading && (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading…
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      {!isLoading && !error && (medications?.length ?? 0) === 0 && (
        <EmptyState
          size="sm"
          accent="amber"
          icon={Pill}
          title="No medications recorded"
          description="Add any medications, treatments, or medical equipment your child uses."
        />
      )}

      {!isLoading && !error && medications && medications.length > 0 && (
        <ul className="space-y-3">
          {medications.map((med) => {
            const expiry = formatExpiration(med.expiresAt);
            return (
              <li
                key={med.id}
                className={cn(ROW, "flex items-start gap-3")}
              >
                <IconChip icon={Pill} accent={SECTION_ACCENT.medications} size="sm" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold text-neutral-900">{med.name}</h3>
                  <p className="mt-0.5 text-sm text-neutral-700">
                    <span className="font-medium">Dosage:</span> {med.dosage}
                    <span className="mx-2 text-neutral-500" aria-hidden>·</span>
                    {FREQUENCY_LABELS[med.frequency]}
                  </p>
                  <p
                    className={`mt-1 text-xs ${expiry.expired ? "font-semibold text-red-700" : "text-neutral-600"}`}
                  >
                    {expiry.label}
                  </p>
                  {med.notes && (
                    <p className="mt-2 text-xs italic text-neutral-600">{med.notes}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(med)}
                    aria-label={`Edit ${med.name}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-neutral-900",
                      FOCUS_RING,
                    )}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(med)}
                    aria-label={`Delete ${med.name}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-red-700",
                      FOCUS_RING,
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <MedicationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        childId={childId}
      />

      <MedicationDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        childId={childId}
        medication={editing}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete medication"
        description={`Are you sure you want to delete ${deleting ? `"${deleting.name}"` : "this medication"}?`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </SectionCard>
  );
}
